import { createFieldStateReducer } from "@/components/form"
import { InputPasswordField } from "@/components/input-password-field"
import { useTranslatedFieldErrorMessage } from "@/hooks/use-translated-field-error-message"
import { trackAnalyticsEvent } from "@/lib/analytics"
import { legacyFilter } from "@/lib/validations/legacyFilter"
import { transformWrongCredentialsError } from "@/lib/validations/wrongCredentials"
import {
  ChangeEventHandler,
  FocusEvent,
  FocusEventHandler,
  useEffect,
} from "react"
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form"
import { LoginFormValues } from "./login-form"

type PasswordInputFieldProps = UseControllerProps<LoginFormValues> & {
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
}

export function LoginFormPasswordInput({
  control,
  onBlur,
}: PasswordInputFieldProps) {
  const { field, fieldState } = useController({ name: "password", control })
  const { setValue } = useFormContext<LoginFormValues>()

  const translatedErrorMessage = useTranslatedFieldErrorMessage(
    fieldState,
    passwordFieldStateReducer,
  )

  useEffect(() => {
    if (translatedErrorMessage) {
      trackAnalyticsEvent({
        event: "link_click",
        event_name: "form_error",
        conversion_pagename: "login",
        error_message: translatedErrorMessage,
        error_type: "password",
      })
    }
  }, [translatedErrorMessage])

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    field.onBlur()
    onBlur?.(event)
  }

  // workaround for legacy behaviour. WARNING: this replaces the user input
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = event.target.value
    const revisedInputValue = legacyFilter(event.target.value)

    field.onChange(event)

    if (inputValue !== revisedInputValue) {
      setValue("password", revisedInputValue)
    }
  }

  return (
    <InputPasswordField
      label="LoginPage.LoginForm.passwordFieldLabel"
      maxLength={20}
      id="password"
      {...field}
      fieldStateReducer={passwordFieldStateReducer}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  )
}

export const PASSWORD_REQUIRED_ERROR = "Errors.passwordRequiredError"

const passwordFieldStateReducer = createFieldStateReducer({
  transformErrorMessage: transformWrongCredentialsError,
})
